import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { trackAppcuesEvent } from "@expert/monitoring";
import { useCallIntroStore, useVerificationFlowStore, useVerifiedCustomerStore } from "@expert/sales-tools";
import { Flex, Stack } from "@mantine/core";
import { useEffect } from "react";

import { useIVRInfo } from "@/customer-info/api";
import { CallIntroFlow } from "@/customer-panel/components/call-intro-flow/CallIntroFlow";
import { CustomerVerification } from "@/customer-panel/components/customer-verification/CustomerVerification";
import { useTaskMdn } from "@/customer-panel/hooks/useTaskMdn";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { useEligibilityPrecheck } from "@/sales-tools/home-product/hooks/useEligibilityPrecheck";
import { useLogger, useSessionId, useTypedMostRecentTask } from "@/sdk";

import { useDigitalCompetencyListener } from "../customer-insights/digital-competency";
import { CustomerOffers } from "../customer-offers/CustomerOffers";
import { calcIsEditing, calcShouldShowCustomerProfile } from "./CustomerPanel.helpers";
import classes from "./CustomerPanel.module.css";
import { CustomerProfile } from "./CustomerProfile";

export function CustomerPanel() {
    const logger = useLogger({ module: "CustomerPanel" });
    const { isChat, voiceTask, chatTask } = useTypedMostRecentTask();
    const sessionId = useSessionId();
    const stepHistory = useVerificationFlowStore((state) => state.getStepHistory(sessionId));
    const callIntroStep = useCallIntroStore((state) => state.currentStep);
    const callIntroStepHistory = useCallIntroStore((state) => state.stepHistory);

    const isCustomerFlowComplete = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(sessionId).isCustomerVerificationComplete,
    );
    const {
        features: { isEnhancedCustomerSearchEnabled },
        blocklistedNumbers = [],
        blocklistedPrefixes = [],
    } = usePartnerConfig();
    const {
        features: { isPreviousSessionSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();

    useDigitalCompetencyListener();
    useEligibilityPrecheck(sessionId);

    const ivrInfo = useIVRInfo();

    const taskMdn = useTaskMdn({
        blocklistedNumbers,
        blocklistedPrefixes,
    });

    // Reset the offer status received flag when the component mounts
    useEffect(() => {
        trackAppcuesEvent("CustomerPanelVisible", {});
    }, []);

    // NOTE: This sets customer verification completion state as a side effect of step history changes.
    useEffect(() => {
        const isFlowComplete =
            callIntroStep === "IntroFlowCompleted" || stepHistory.at(stepHistory.length - 1) === "VerificationComplete";
        if (isFlowComplete !== isCustomerFlowComplete) {
            useVerifiedCustomerStore.getState().setIsCustomerVerificationComplete(sessionId, isFlowComplete);
        }
    }, [stepHistory, callIntroStep, isCustomerFlowComplete, sessionId]);

    if (!chatTask && !voiceTask) {
        logger.error("Customer Panel | Unable to find most recent task, customer panel will not display");
        return undefined;
    }

    const isEditing = calcIsEditing({
        isCustomerFlowComplete,
        stepHistory,
        callIntroStepHistory,
    });

    const shouldShowCustomerProfile = calcShouldShowCustomerProfile({
        isChat,
        isCustomerFlowComplete,
        isEditing,
        callIntroStep,
        stepHistory,
        isEnhancedCustomerSearchEnabled,
        ivrInfo,
    });

    return (
        <Flex className={classes.customerPanel} data-testid="customer-panel">
            <Flex className={classes.responsivePanel}>
                {shouldShowCustomerProfile && (
                    <Flex className={classes.topSection}>
                        <CustomerProfile />
                    </Flex>
                )}

                {!isCustomerFlowComplete ? (
                    isEnhancedCustomerSearchEnabled ? (
                        <CallIntroFlow />
                    ) : (
                        <CustomerVerification
                            mdnFromTask={taskMdn}
                            key={sessionId}
                            isPreviousSessionSummaryEnabled={isPreviousSessionSummaryEnabled}
                        />
                    )
                ) : (
                    <Stack mt={"sm"}>
                        <CustomerOffers />
                    </Stack>
                )}
            </Flex>
        </Flex>
    );
}
